import React, { useState, useRef, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Tooltip from '../../../components/ui/Tooltip';


const PropertyInfoStep = ({
  formData,
  onChange,
  onPhotoUpload,
  onPhotoRemove,
  errors = {},
  uploadingPhotos = false,
  onAnalyzeUrl,
  analyzingUrl = false,
  subscriptionTier = 'free'
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const dropZoneRef = useRef(null);
  const fileInputRef = useRef(null);

  const propertyTypes = [
    { value: 'multiplex', label: 'Multiplex' },
    { value: 'rental_condo', label: 'Condo locatif' },
    { value: 'short_term', label: 'Court terme' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'other', label: 'Autre' }
  ];

  const handleFileChange = (e) => {
    const files = Array.from(e?.target?.files);
    if (files?.length > 0) {
      onPhotoUpload(files);
    }
  };

  // Handle drag and drop
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the drop zone
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith('image/'));
    if (files.length > 0) {
      onPhotoUpload(files);
    }
  };

  // Handle paste from clipboard
  useEffect(() => {
    const handlePaste = async (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const imageFiles = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.indexOf('image') !== -1) {
          const file = item.getAsFile();
          if (file) {
            imageFiles.push(file);
          }
        }
      }

      if (imageFiles.length > 0) {
        onPhotoUpload(imageFiles);
      }
    };

    // Add paste listener to the document
    document.addEventListener('paste', handlePaste);
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [onPhotoUpload]);

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="bg-card rounded-xl border border-border p-4 md:p-6 lg:p-8 mb-4 md:mb-6">
        <div className="flex items-center gap-3 mb-4 md:mb-6">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Building2" size={24} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">
              Informations de la propriété
            </h2>
            <p className="text-xs md:text-sm text-muted-foreground">
              Détails de base et caractéristiques
            </p>
          </div>
        </div>

        <div className="space-y-4 md:space-y-6">
          {/* AI Analysis by URL - Available for all users */}
          {onAnalyzeUrl && (
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <Icon name="Sparkles" size={20} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-foreground mb-1 flex items-center gap-1.5">
                    Analyse automatique par URL
                    <Tooltip content="Collez un lien Centris ou Realtor pour importer automatiquement les photos et les détails." />
                  </h3>
                  <p className="text-xs text-muted-foreground mb-3">
                    Collez l'URL d'une annonce immobilière (Centris, DuProprio, etc.) pour remplir automatiquement les champs
                  </p>
                  <div className="flex gap-2">
                    <input
                      id="property-url-input"
                      type="url"
                      placeholder="https://www.centris.ca/..."
                      className="flex-1 px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      disabled={analyzingUrl}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !analyzingUrl) {
                          const url = e.target.value;
                          if (url) {
                            onAnalyzeUrl(url);
                            e.target.value = '';
                          }
                        }
                      }}
                    />
                    <button
                      onClick={() => {
                        const input = document.getElementById('property-url-input');
                        const url = input?.value;
                        if (url && !analyzingUrl) {
                          onAnalyzeUrl(url);
                          input.value = '';
                        }
                      }}
                      disabled={analyzingUrl}
                      className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {analyzingUrl ? (
                        <>
                          <Icon name="Loader2" size={16} className="animate-spin" />
                          Analyse...
                        </>
                      ) : (
                        <>
                          <Icon name="Sparkles" size={16} />
                          Analyser
                        </>
                      )}
                    </button>
                  </div>
                  {errors?.urlAnalysis && errors.urlAnalysis !== 'success' && (
                    <p className="text-xs text-destructive mt-2">
                      {errors.urlAnalysis}
                    </p>
                  )}
                  {errors?.urlAnalysis === 'success' && (
                    <p className="text-xs text-success mt-2 flex items-center gap-1">
                      <Icon name="Check" size={14} />
                      Analyse terminée avec succès !
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <Input
            label="Adresse complète"
            type="text"
            placeholder="123 Rue Principale, Montréal, QC H1A 1A1"
            value={formData?.address || ''}
            onChange={(e) => onChange('address', e?.target?.value)}
            error={errors?.address}
            required
          />

          <Input
            label="URL de l'immeuble"
            type="url"
            placeholder="https://www.centris.ca/..."
            value={formData?.propertyUrl || ''}
            onChange={(e) => onChange('propertyUrl', e?.target?.value)}
            error={errors?.propertyUrl}
            description="Lien vers la fiche de l'immeuble (Centris, DuProprio, etc.)"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Type de propriété"
              tooltip="La catégorie de l'immeuble (Multiplex, Condo, etc.). Influence les ratios et les calculs de rentabilité."
              options={propertyTypes}
              value={formData?.propertyType || ''}
              onChange={(value) => onChange('propertyType', value)}
              error={errors?.propertyType}
              required
              placeholder="Sélectionner le type"
            />

            <Input
              label="Nombre d'unités"
              type="number"
              placeholder="Ex: 4"
              value={formData?.units || ''}
              onChange={(e) => onChange('units', e?.target?.value)}
              error={errors?.units}
              required
              min="1"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Superficie (pi²)"
              type="number"
              placeholder="Ex: 2500"
              value={formData?.squareFeet || ''}
              onChange={(e) => onChange('squareFeet', e?.target?.value)}
              error={errors?.squareFeet}
            />

            <Input
              label="Année de construction"
              tooltip="L'âge de l'immeuble aide à estimer les besoins futurs en entretien et le niveau de risque."
              type="number"
              placeholder="Ex: 1985"
              value={formData?.yearBuilt || ''}
              onChange={(e) => onChange('yearBuilt', e?.target?.value)}
              error={errors?.yearBuilt}
              min="1800"
              max={new Date()?.getFullYear()}
            />

            <Input
              label="Nombre de chambres total"
              type="number"
              placeholder="Ex: 12"
              value={formData?.totalBedrooms || ''}
              onChange={(e) => onChange('totalBedrooms', e?.target?.value)}
              error={errors?.totalBedrooms}
              min="0"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Photos de la propriété
            </label>
            <div
              ref={dropZoneRef}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-4 md:p-6 text-center transition-all ${isDragging
                ? 'border-primary bg-primary/5 scale-[1.02]'
                : 'border-border hover:border-primary/50'
                } ${uploadingPhotos ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <input
                ref={fileInputRef}
                type="file"
                id="property-photos"
                multiple
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                disabled={uploadingPhotos}
              />
              <label
                htmlFor="property-photos"
                className={`flex flex-col items-center gap-2 ${uploadingPhotos ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <div className="w-12 h-12 md:w-16 md:h-16 bg-muted rounded-full flex items-center justify-center">
                  {uploadingPhotos ? (
                    <Icon name="Loader2" size={24} color="var(--color-muted-foreground)" className="animate-spin" />
                  ) : (
                    <Icon name="Upload" size={24} color="var(--color-muted-foreground)" />
                  )}
                </div>
                <div>
                  <p className="text-sm md:text-base font-medium text-foreground">
                    {uploadingPhotos
                      ? 'Téléchargement en cours...'
                      : isDragging
                        ? 'Déposez les images ici'
                        : 'Cliquez, glissez-déposez ou Ctrl+V'}
                  </p>
                  <p className="text-xs md:text-sm text-muted-foreground mt-1">
                    PNG, JPG, WEBP jusqu'à 5MB chacun
                  </p>
                  <p className="text-xs text-muted-foreground mt-1 flex items-center justify-center gap-1 flex-wrap">
                    <span className="flex items-center gap-1">
                      <Icon name="MousePointer2" size={12} />
                      Cliquez
                    </span>
                    <span className="mx-1">•</span>
                    <span className="flex items-center gap-1">
                      <Icon name="Move" size={12} />
                      Glissez-déposez
                    </span>
                    <span className="mx-1">•</span>
                    <span className="flex items-center gap-1 font-medium text-primary">
                      <Icon name="Command" size={12} />
                      Ctrl+V
                    </span>
                  </p>
                </div>
              </label>
            </div>
            {errors?.photos && (
              <p className="text-sm text-destructive mt-2">{errors?.photos}</p>
            )}

            {/* ✅ Aperçu photo importée (URL) — sans polluer formData.photos */}
            {(!formData?.photos?.length && formData?.mainImageUrl) && (
              <div className="mt-4">
                <p className="text-xs text-muted-foreground mb-2">
                  Photo importée (aperçu)
                </p>

                <div className="relative group max-w-[260px]">
                  <div className="aspect-square rounded-lg overflow-hidden bg-muted border border-border">
                    <Image
                      src={formData.mainImageUrl}
                      alt={formData.mainImageAlt || 'Photo importée'}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Optionnel: bouton pour “retirer” l’aperçu importé */}
                  <button
                    type="button"
                    onClick={() => {
                      onChange('mainImageUrl', '');
                      onChange('mainImageAlt', '');
                    }}
                    className="absolute top-2 right-2 w-8 h-8 bg-destructive rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Retirer la photo importée"
                  >
                    <Icon name="X" size={16} color="var(--color-destructive-foreground)" />
                  </button>
                </div>
              </div>
            )}


            {formData?.photos && formData?.photos?.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 mt-4">
                {formData?.photos?.map((photo, index) => (
                  <div key={index} className="relative group">
                    <div className="aspect-square rounded-lg overflow-hidden bg-muted">
                      <Image
                        src={photo?.url}
                        alt={photo?.alt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => onPhotoRemove(index)}
                      className="absolute top-2 right-2 w-8 h-8 bg-destructive rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Icon name="X" size={16} color="var(--color-destructive-foreground)" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Select
            label="Étape de l'immeuble"
            options={[
              { value: 'analysis', label: 'Analyse' },
              { value: 'visit', label: 'Visite' },
              { value: 'offer_made', label: 'Offre faite' },
              { value: 'offer_accepted', label: 'Offre acceptée' },
              { value: 'acquired', label: 'Propriété acquise' }
            ]}
            value={formData?.stage || 'analysis'}
            onChange={(value) => onChange('stage', value)}
            description="Étape actuelle dans le processus d'acquisition"
          />

          <Input
            label="Notes additionnelles"
            type="text"
            placeholder="Informations supplémentaires sur la propriété..."
            value={formData?.notes || ''}
            onChange={(e) => onChange('notes', e?.target?.value)}
          />
        </div>
      </div>
    </div>
  );
};

export default PropertyInfoStep;